import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { resolvePostEmbed, safeExternalUrl, type PostEmbed } from '@/utils/postEmbed';
import PostViewerModal, { type OpenPost } from './PostViewerModal';

export interface OpenPostOptions {
  title?: string;
  // Embed ya resuelto por quien llama (p. ej. desde la clave del post), cuando la URL sola no basta.
  embed?: PostEmbed | null;
}

interface PostViewerValue {
  openPost: (url: string | null | undefined, options?: OpenPostOptions) => void;
}

function openInNewTab(url: string | null | undefined): void {
  const safe = safeExternalUrl(url);
  if (safe) window.open(safe, '_blank', 'noopener,noreferrer');
}

// Sin provider (tests aislados, piezas sueltas) el enlace se comporta como siempre: pestaña nueva.
const PostViewerContext = createContext<PostViewerValue | null>(null);

/**
 * Monta un unico visor de publicaciones para todo lo que cuelga de el. Se anida dentro de
 * otros Dialogs (drawers) para que el visor quede como hijo suyo y el drawer no se cierre
 * al hacer clic en el visor.
 */
export const PostViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [post, setPost] = useState<OpenPost | null>(null);

  const openPost = useCallback<PostViewerValue['openPost']>((url, options) => {
    const safe = safeExternalUrl(url);
    // Con un embed ya resuelto se abre aunque no haya URL valida: solo falta el enlace de salida.
    const embed = options?.embed ?? resolvePostEmbed(safe);
    if (!embed) {
      openInNewTab(safe);
      return;
    }
    setPost({ url: safe, embed, title: options?.title });
  }, []);

  const value = useMemo(() => ({ openPost }), [openPost]);

  return (
    <PostViewerContext.Provider value={value}>
      {children}
      <PostViewerModal post={post} onClose={() => setPost(null)} />
    </PostViewerContext.Provider>
  );
};

const FALLBACK: PostViewerValue = { openPost: url => openInNewTab(url) };

export function usePostViewer(): PostViewerValue & { inViewer: boolean } {
  const value = useContext(PostViewerContext);
  return { ...(value ?? FALLBACK), inViewer: value !== null };
}
